import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Card, Spinner, Text } from '@shopify/polaris'
import HighlightableDataTable from 'components/HighlightableDataTable/index'
import NoDataMessage from 'components/NoDataMessage/NoDataMessage'
import { AlertIcon } from 'img/icons/components'
import { ReactComponent as CalendarImg } from 'img/icons/misc/calendar-img.svg'
import { isTaxAgentSelector } from '../../../../redux/selectors'
import formatRow from 'utils/formatRow'
import _ from 'lodash'
import {
	sortDueDateRowsByStatus,
	formatDueDateCountry,
	getDueDatePeriodTypeLabel,
	getDueDateNavigation,
} from '../../../DueDates/utils'
import { REPORT_CALENDAR_PATH } from '../../../../projectConfig/navigation/reportCalendarNav'

const DueDatesSection = ({ dueDates, fetchingDueDates, user, t, navigate }) => {
	const openProjectName = useSelector((state) => state.user?.openProjectName)
	const IS_TAX_AGENT = isTaxAgentSelector(user)
	const rows = useMemo(() => sortDueDateRowsByStatus(dueDates.items ?? []), [dueDates.items])

	const handleFillClick = (rowData) => {
		const { pathname, state } = getDueDateNavigation(rowData, openProjectName)
		navigate(pathname, state ? { state } : undefined)
	}

	const columns = useMemo(() => [
		{
			property: "name",
			cell: {
				formatters: [
					(value, { rowData }) => {
						// Use name field if available, otherwise fallback to type_of_report
						return value || rowData.type_of_report || "";
					},
				],
			},
		},
		{
			property: "country",
			cell: {
				formatters: [
					(value, { rowData }) => {
						return formatDueDateCountry(value, rowData)
					},
				],
			},
		},
		{
			property: "period_type",
			cell: {
				formatters: [
					(value, { rowData }) => {
						return getDueDatePeriodTypeLabel(rowData, t, 'home')
					},
				],
			},
		},
		{
			cell: {
				formatters: [
					(value, { rowData }) =>
						`${rowData.period} ${rowData.period_type} ${rowData.period_year}`,
				],
			},
		},
		{
			property: "exp_date",
		},
		{
			property: "",
			cell: {
				formatters: [
					(value, { rowData }) => {
						return (
							(rowData.status === "red" || rowData.status === "yellow") && (
								<AlertIcon
									stroke={rowData.status === "red" ? "#F97066" : "#B8821B"}
								/>
							)
						)
					},
				],
			},
		},
		{
			property: "",
			cell: {
				formatters: [
					(value, { rowData }) => {
						return (
							<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
								{!IS_TAX_AGENT ? (
									<div
										style={{
											height: '32px',
											background: 'rgba(38, 127, 215, 1)',
											color: '#fff',
											borderColor: 'transparent',
											boxShadow: 'none',
											borderRadius: 6,
											width: 93,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											cursor: 'pointer',
											fontSize: 12
										}}
										onClick={() => handleFillClick(rowData)}
									>
										{t("home.fillVR")}
									</div>
								) : (
									<Text>{rowData.company_name}</Text>
								)}
							</div>
						)
					},
				],
			},
		},
	], [IS_TAX_AGENT, t, navigate, openProjectName])

	const sortedRows = useMemo(() => formatRow(rows, columns), [rows, columns])

	return (
		<Card sectioned={fetchingDueDates || _.isEmpty(rows)}>
			{fetchingDueDates ? (
				<Spinner />
			) : (
				<>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: 'flex-start' }}>
						<h2 style={{ fontSize: 18, fontWeight: '600' }}>{t("home.title.yourDD")}</h2>
						<button
							style={{
								maxHeight: '20px',
								borderColor: 'transparent',
								boxShadow: 'none',
								background: 'transparent',
								height: 'auto',
								color: 'rgba(44, 110, 203, 1)',
								cursor: 'pointer',
								fontSize: 14,
								fontWeight: 400,
							}}
							onClick={() => navigate(REPORT_CALENDAR_PATH)}
						>
							{t("home.viewAll")}
						</button>
					</div>
					{!_.isEmpty(rows) && !fetchingDueDates && (
						<>
							<br />
							<div className='border-top table-dashboard-returns'>
								<HighlightableDataTable
									headings={[]}
									rows={sortedRows}
									rawItems={rows}
									rowClassNameCallback={(row, rowData) => ({
										"bg-red": rowData.status === "red",
										"bg-yellow": rowData.status === "yellow",
									})}
									styles={{
										td: {
											padding: "0.3rem 0.85rem",
											fontSize: 14,
											fontWeight: '400',
										},
									}}
								/>
							</div>
						</>
					)}
					{_.isEmpty(rows) && !fetchingDueDates && (
						<div>
							<NoDataMessage
								title={t("home.noDataYet")}
								description={t("home.addStoreToSeeData")}
								buttonText={t("home.addStore")}
								Image={CalendarImg}
								style={{ height: "auto" }}
								onClick={() => navigate("/stores/add")}
							/>
						</div>
					)}
				</>
			)}
		</Card>
	)
}

export default DueDatesSection

